import * as React from 'react'
import { clsx } from 'clsx'
import type { EventBlock, EventField } from '@basehub/data/replicache/index'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import * as PropertiesPanel from '../../properties-panel'
import {
  CalendarIcon,
  CaretDownIcon,
  CheckboxIcon,
  Cross2Icon,
  DotsHorizontalIcon,
  EnvelopeClosedIcon,
  EyeNoneIcon,
  FileTextIcon,
  FrameIcon,
  HamburgerMenuIcon,
  InputIcon,
  PaperPlaneIcon,
  PlusIcon,
  RadiobuttonIcon,
  TableIcon,
  TextIcon,
  TrashIcon,
} from '@radix-ui/react-icons'
import { Button } from '~/sotano-kit/primitives/button'
import { Input } from '~/sotano-kit/primitives/input'
import { IconButton } from '~/sotano-kit/primitives/icon-button'
import { getIdForHTML } from '@basehub/utils/id/index'
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from '~/sotano-kit/primitives/popover'
import { PopoverAnchor } from '@radix-ui/react-popover'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '~/sotano-kit/primitives/accordion'
import { IconProps } from '@radix-ui/react-icons/dist/types'
import { useEditorMode } from '../../editor-mode-provider'
import { ScrollArea } from '~/sotano-kit/primitives/scroll-area'
import { useSyncedValue } from '~/hooks/use-synced-value'
import { EventSchemaHelpers, isBranch } from '@basehub/data/replicache/index'
import { camelCase } from 'change-case'
import { BarChartIcon } from '~/components/icons/bar-chart'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { UpdateBlockParams } from '@basehub/data/replicache/mutators/repo-space/block/mutators/crud'
import { useRepoUndoManager } from '~/replicache/frontend/undo-redo/manager'
import { getBlockFocusOp } from '~/replicache/frontend/undo-redo/util'
import { useTabsStore } from '../../tabs/store'
import { BodySchema_POST } from '~/app/api/v2/events/schema'
import { toast } from 'sonner'
import { useSelectedBranchOrCommitQuery } from '~/lib/commits/use-selected-branch-or-commit'
import { faker } from '@faker-js/faker'
import { ResetInstanceIcon } from '~/components/icons/reset-instance'
import { useUserPreferences } from '~/hooks-data/use-user-preferences'

type EventBlockLayouts = EventBlock['typeOptions']['view']

export const EventBlockPropertiesPanel = ({
  block,
  path,
}: {
  block: EventBlock
  path: string
}) => {
  const { data } = useRepoReplicache()
  const replicache = data?.replicache
  const teamId = data?.team.id
  const repoId = data?.repo.id
  const undoManager = useRepoUndoManager()
  const activeTabKey = useTabsStore((s) => s.activeTab?.key)
  const sourceBlockId = idToSourceBlockId(block)
  const getInputFocusOp = React.useCallback(
    (id: string | undefined) => {
      return getBlockFocusOp({
        block: {
          id: block.id,
          sourceBlockId: block.sourceBlockId,
        },
        preferredTabKey: activeTabKey,
        focusType: 'block:properties-panel',
        elementIdOverride: id,
      })
    },
    [block.id, block.sourceBlockId, activeTabKey]
  )
  const { data: branchOrCommit } = useSelectedBranchOrCommitQuery()

  return (
    <PropertiesPanel.PropertiesContainer>
      <PropertiesPanel.Group title="Layout" spacing="tight">
        <PropertiesPanel.LayoutOption
          direction="vertical"
          options={[
            {
              id: 'table' satisfies EventBlockLayouts,
              title: 'Table',
              icon: <TableIcon width={18} height={18} />,
              description: 'For inline visualization',
            },
            {
              id: 'chart' satisfies EventBlockLayouts,
              title: 'Time-series',
              icon: <BarChartIcon width={18} height={18} />,
              description: 'For graphic visualization',
            },
          ]}
          value={block.typeOptions.view}
          id={`el-${sourceBlockId}-pp-layout`}
          onValueChange={async (v) => {
            if (!replicache) return

            const params: UpdateBlockParams = {
              id: block.id,
              instanceChildTarget: block.instanceChildTarget,
              path,
              data: {
                type: 'event',
                typeOptions: { view: v.id as EventBlockLayouts },
              },
              strategy: 'merge-deep',
              isoNow: new Date().toISOString(),
            }
            const result = await replicache.mutate.updateBlock(params)

            if (result?.undo) {
              undoManager.current?.push({
                redo: [
                  getInputFocusOp(`el-${sourceBlockId}-pp-layout`),
                  { op: 'updateBlock', args: params },
                ],
                undo: [
                  getInputFocusOp(`el-${sourceBlockId}-pp-layout`),
                  {
                    op: 'blockBatchMutations',
                    args: { mutations: result.undo },
                  },
                ],
              })
            }
          }}
        />
      </PropertiesPanel.Group>
      <SchemaOption block={block} path={path} />
      <PropertiesPanel.Group spacing="tight">
        <Button
          intent="secondary"
          id={`el-${sourceBlockId}-pp-send-test-event`}
          onClick={async () => {
            if (!branchOrCommit) return
            try {
              const res = await fetch(
                `/api/ad-hoc/get-event-key?blockId=${idToSourceBlockId(
                  block
                )}&teamId=${teamId}&repoId=${repoId}&branchId=${isBranch(branchOrCommit) ? branchOrCommit.id : branchOrCommit.branchId}`
              )
              const key = await res.text()
              const res2 = await fetch('/api/v2/events', {
                method: 'POST',
                body: JSON.stringify({
                  type: 'create',
                  key,
                  data: generateFakeDataFromEventSchema({
                    ...block.constraints.schema,
                    ...block.value?.schema,
                  }),
                } satisfies BodySchema_POST),
              })
              if (res2.ok) {
                toast.success('Test event sent')
              } else {
                toast.error('Failed to send test event')
              }
            } catch (e) {
              console.error(e)
              toast.error('Failed to send test event')
            }
          }}
          icon={<PaperPlaneIcon className="mr-0.5" />}
        >
          Send test event
        </Button>
      </PropertiesPanel.Group>
    </PropertiesPanel.PropertiesContainer>
  )
}

const SchemaOption = ({ block, path }: { block: EventBlock; path: string }) => {
  const editorMode = useEditorMode()
  const editMode = useUserPreferences((s) => s.editMode)
  const isOverriding =
    (editorMode === 'write:value-only' ||
      editorMode === 'write:value-and-title') &&
    !editMode
  const hasOverrides = Object.keys(block.value?.schema ?? {}).length > 0
  const readOnly = editorMode !== 'write:all' && !isOverriding
  const replicache = useRepoReplicache().data?.replicache

  const schema = React.useMemo(
    () => ({ ...block.constraints.schema, ...block.value?.schema }),
    [block.constraints.schema, block.value?.schema]
  )
  const schemaRef = React.useRef(schema)
  schemaRef.current = schema

  const entries = EventSchemaHelpers.withUniqueFieldNames(schema)
  const lastFieldOrder = React.useMemo(
    () => entries.at(-1)?.order ?? 0,
    [entries]
  )

  const handleNewField = React.useCallback(async () => {
    if (!replicache?.mutate) return
    const id = getIdForHTML()
    const newField = {
      type: 'text',
      required: false,
      defaultValue: '',
      label: 'New Field',
      order: lastFieldOrder + 1,
    } satisfies Omit<EventField, 'name'>

    const uniqueName = EventSchemaHelpers.getUniqueFieldName(
      { id, ...newField },
      schemaRef.current
    ).name

    const params: UpdateBlockParams = {
      id: block.id,
      instanceChildTarget: block.instanceChildTarget,
      path,
      isoNow: new Date().toISOString(),
      data: {
        type: block.type,
        ...(!isOverriding
          ? {
              constraints: {
                schema: { [id]: { ...newField, name: uniqueName } },
              },
            }
          : { value: { schema: { [id]: { ...newField, name: uniqueName } } } }),
      },
      strategy: 'merge-deep',
    }

    await replicache.mutate.updateBlock(params)

    setTimeout(() => {
      const lastInput = document.querySelector(
        `input#pp-schema-field-${id}`
      ) as HTMLInputElement | null
      lastInput?.focus()
      lastInput?.select()
    }, 20)
  }, [
    replicache?.mutate,
    lastFieldOrder,
    block.id,
    block.type,
    block.instanceChildTarget,
    path,
    isOverriding,
  ])

  const handleFieldUpdate = React.useCallback(
    async (id: string, field: Partial<EventField>) => {
      if (field.name) {
        const normalized = camelCase(field.name)
        if (
          normalized === schemaRef.current[id]?.name &&
          Object.keys(field).length === 1
        ) {
          // If the name is the same and there are no other changes, don't update
          return { error: null }
        }

        if (
          normalized &&
          normalized !== schemaRef.current[id]?.name &&
          EventSchemaHelpers.checkIfFieldNameExists(
            { name: normalized, id },
            schemaRef.current
          )
        ) {
          return { error: 'Field name already exists' }
        }
        field.name = normalized
      }

      await replicache?.mutate.updateBlock({
        id: block.id,
        instanceChildTarget: block.instanceChildTarget,
        data: {
          type: block.type,
          ...(!isOverriding
            ? { constraints: { schema: { [id]: field } } }
            : {
                value: {
                  schema: {
                    [id]: block.value.schema?.[id]
                      ? field
                      : { ...schemaRef.current[id], ...field },
                  },
                },
              }),
        },
        strategy: 'merge-deep',
        isoNow: new Date().toISOString(),
        path,
      })

      return { error: null }
    },
    [
      block.id,
      block.value.schema,
      block.type,
      block.instanceChildTarget,
      path,
      replicache?.mutate,
      isOverriding,
    ]
  )

  const handleFieldDelete = React.useCallback(
    (fieldId: string) => {
      return replicache?.mutate.updateBlock({
        id: block.id,
        instanceChildTarget: block.instanceChildTarget,
        data: {
          type: block.type,
          ...(!isOverriding
            ? { constraints: { schema: { [fieldId]: null } } }
            : { value: { schema: { [fieldId]: null } } }),
        },
        strategy: 'merge-deep',
        isoNow: new Date().toISOString(),
        path,
      })
    },
    [
      block.id,
      block.type,
      block.instanceChildTarget,
      path,
      replicache?.mutate,
      isOverriding,
    ]
  )

  const handleResetOverrides = React.useCallback(() => {
    if (!isOverriding) return
    return replicache?.mutate.updateBlock({
      id: block.id,
      instanceChildTarget: block.instanceChildTarget,
      data: {
        type: block.type,
        value: { schema: {} },
      },
      strategy: 'merge',
      isoNow: new Date().toISOString(),
      path,
    })
  }, [
    block.id,
    block.type,
    block.instanceChildTarget,
    isOverriding,
    path,
    replicache?.mutate,
  ])

  return (
    <PropertiesPanel.Group
      title={
        <div className="flex justify-between w-full">
          <span>Schema</span>
          {!readOnly && (
            <div className="flex items-center gap-x-1">
              {isOverriding && hasOverrides && (
                <IconButton
                  tooltip={{ content: 'Reset overrides', side: 'left' }}
                  onClick={handleResetOverrides}
                >
                  <ResetInstanceIcon className="size-3.5" />
                </IconButton>
              )}
              <IconButton
                tooltip={{ content: 'Add new field', side: 'left' }}
                onClick={handleNewField}
              >
                <PlusIcon className="size-3.5" />
              </IconButton>
            </div>
          )}
        </div>
      }
      titleClassName="!mb-2"
    >
      {!entries.length ? (
        <span className="relative text-xs leading-normal text-dark-faint -top-1">
          Inputs will show up here.
        </span>
      ) : (
        entries.map((field) => (
          <FieldOption
            key={field.id}
            {...field}
            readOnly={readOnly}
            onDelete={handleFieldDelete}
            onUpdate={handleFieldUpdate}
            getFieldName={(label) =>
              EventSchemaHelpers.getUniqueFieldName(
                { label, id: field.id },
                schema
              ).name
            }
          />
        ))
      )}
    </PropertiesPanel.Group>
  )
}

const FieldOption = ({
  id,
  onDelete,
  onUpdate,
  getFieldName,
  readOnly,
  ...rest
}: EventField & {
  id: string
  onDelete: (id: string) => void
  onUpdate: (
    id: string,
    field: Partial<EventField>
  ) => Promise<{ error: string | null }>
  getFieldName: (label: string) => string
  readOnly: boolean
}) => {
  const [error, setError] = React.useState<string | null>(null)

  const onNameUpdate = async (value: string) => {
    const { error } = await onUpdate(id, { name: value })
    if (error) {
      setError(error)
    } else {
      setError(null)
    }
  }

  const { value: label, onChange: setLabel } = useSyncedValue({
    value: rest.label,
    setValue: (v) => {
      if (v) {
        onUpdate(id, { label: v, name: getFieldName(v) })
      }
    },
  })

  return (
    <Popover modal>
      <PopoverAnchor asChild>
        <div
          className={clsx(
            'flex items-center',
            readOnly ? 'gap-x-1.5' : 'gap-x-2.5'
          )}
        >
          {readOnly ? (
            <>
              <FieldIcon type={rest.type} className="size-3 text-dark-dim" />
              <span className="text-xs font-medium leading-normal text-dark-dim grow">
                {label}
              </span>
            </>
          ) : (
            <Input
              id={`pp-schema-field-${id}`}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              onBlur={() => label || setLabel('Untitled')}
              containerClassName="flex-grow"
              size="sm"
              icon={
                <FieldIcon
                  type={rest.type}
                  className="size-3 text-dark-muted"
                />
              }
            />
          )}

          <PopoverTrigger asChild>
            <Button
              unstyled
              focusStyled
              className="cursor-default p-1 hover:text-dark-bright text-dark-faint transition-colors"
            >
              <DotsHorizontalIcon className="size-3.5" />
            </Button>
          </PopoverTrigger>
        </div>
      </PopoverAnchor>
      <PopoverContent
        sideOffset={20}
        side="left"
        collisionPadding={{ bottom: 32 }}
        align="start"
        className="max-h-[460px] overflow-hidden grid grid-rows-[auto_1fr_auto] !p-0 w-[280px]"
      >
        <ScrollArea viewportClassName="[&>:last-child]:mb-3">
          <div
            className={clsx(
              // 'sticky top-0 z-modal to-transparent bg-gradient-to-b from-dark-base from-85%',
              'flex items-center justify-between gap-x-1.5 pt-3 pb-2 px-4'
            )}
          >
            <p className="text-sm font-medium leading-normal text-dark-bright">
              {readOnly ? 'Field info' : 'Edit field'}
            </p>
            <PopoverClose asChild>
              <IconButton
                aria-label="Close"
                className="-mr-1 text-dark-faint hover:text-dark-bright hover:!bg-transparent"
              >
                <Cross2Icon />
              </IconButton>
            </PopoverClose>
          </div>
          <div
            className={clsx('px-4 grid', readOnly ? 'gap-y-1.5' : 'gap-y-2')}
          >
            <PropertiesPanel.TextOption
              id="field-label"
              className="[&>:first-child>*]:!min-w-0 [&>:first-child]:min-w-[5.75rem]"
              horizontal
              name="Label"
              value={rest.label ?? ''}
              mutate={(v) => onUpdate(id, { label: v })}
              forceEditable={!readOnly}
            />
            <PropertiesPanel.TextOption
              id="field-name"
              className="[&>:first-child>*]:!min-w-0 [&>:first-child]:min-w-[5.75rem]"
              horizontal
              value={rest.name}
              name="Name"
              mutate={onNameUpdate}
              forceEditable={!readOnly}
            />
            {error && <span className="text-xs text-dark-error">{error}</span>}
            <PropertiesPanel.SelectOption
              horizontal
              labelClassName="font-medium !text-xs min-w-[5.75rem]"
              title="Type"
              id={`${id}-pp-type`}
              options={[
                {
                  id: 'text',
                  label: 'Text',
                  icon: <FieldIcon type="text" />,
                },
                {
                  id: 'textarea',
                  label: 'Text Area',
                  icon: <FieldIcon type="textarea" />,
                },
                {
                  id: 'email',
                  label: 'Email',
                  icon: <FieldIcon type="email" />,
                },
                {
                  id: 'checkbox',
                  label: 'Checkbox',
                  icon: <FieldIcon type="checkbox" />,
                },
                {
                  id: 'number',
                  label: 'Number',
                  icon: <FieldIcon type="number" />,
                },
                {
                  id: 'select',
                  label: 'Select',
                  icon: <FieldIcon type="select" />,
                },
                {
                  id: 'file',
                  label: 'File',
                  icon: <FieldIcon type="file" />,
                },
                {
                  id: 'radio',
                  label: 'Radio',
                  icon: <FieldIcon type="radio" />,
                },
                {
                  id: 'date',
                  label: 'Date',
                  icon: <FieldIcon type="date" />,
                },
                {
                  id: 'datetime',
                  label: 'Datetime',
                  icon: <FieldIcon type="datetime" />,
                },
                {
                  id: 'hidden',
                  label: 'Hidden',
                  icon: <FieldIcon type="hidden" />,
                },
                // {
                //   id: 'file',
                //   label: 'File',
                //   icon: <FieldIcon type="file" />,
                // },
              ]}
              value={rest.type}
              handleValueChange={(v) =>
                onUpdate(id, { type: v as EventField['type'] })
              }
              forceEditable={!readOnly}
            />
            {(rest.type === 'select' || rest.type === 'radio') && (
              <>
                <OptionsOption
                  name="Options"
                  value={rest.options}
                  mutate={(v) => onUpdate(id, { options: v })}
                  readOnly={readOnly}
                />
                <PropertiesPanel.BooleanOption
                  readOnlyClassName="!text-xs !justify-start gap-x-1 [&>:first-child]:min-w-[5.75rem] [&>:first-child]:text-dark-muted [&>:first-child]:font-medium [&>:first-child]:leading-7"
                  className="!text-xs"
                  name="Multiple"
                  id="field-multiple"
                  value={rest.multiple}
                  mutate={(v) => onUpdate(id, { multiple: v })}
                  forceEditable={!readOnly}
                />
              </>
            )}
            <PropertiesPanel.BooleanOption
              readOnlyClassName="!text-xs !justify-start gap-x-1 [&>:first-child]:min-w-[5.75rem] [&>:first-child]:text-dark-muted [&>:first-child]:font-medium [&>:first-child]:leading-7"
              className="!text-xs"
              name="Required"
              id="field-required"
              value={rest.required}
              mutate={(v) => onUpdate(id, { required: v })}
              forceEditable={!readOnly}
            />
            <Accordion
              type="single"
              collapsible
              defaultValue={readOnly ? 'extra' : undefined}
            >
              <AccordionItem value="extra">
                <AccordionTrigger
                  className={clsx(
                    readOnly && 'hidden',
                    'flex gap-x-0.5 items-center cursor-default hover:text-dark-muted text-dark-faint text-xs font-medium group mt-1 outline-1 outline-offset-2 outline-dark-control-base'
                  )}
                >
                  Extra Properties
                  <CaretDownIcon className="size-5 shrink-0 group-data-[state=open]:rotate-180 transition-transform duration-500" />
                </AccordionTrigger>
                <AccordionContent
                  className={clsx('grid', !readOnly && 'gap-y-2')}
                >
                  <PropertiesPanel.TextOption
                    id="field-placeholder"
                    className={clsx(
                      !readOnly && 'pt-2',
                      '[&>:first-child>*]:!min-w-0 [&>:first-child]:min-w-[5.75rem]'
                    )}
                    horizontal
                    name="Placeholder"
                    value={rest.placeholder ?? ''}
                    mutate={(v) => onUpdate(id, { placeholder: v })}
                    forceEditable={!readOnly}
                  />
                  <PropertiesPanel.TextOption
                    id="field-helpText"
                    className="[&>:first-child>*]:!min-w-0 [&>:first-child]:min-w-[5.75rem]"
                    horizontal
                    value={rest.helpText ?? ''}
                    name="Help Text"
                    mutate={(v) => onUpdate(id, { helpText: v })}
                    forceEditable={!readOnly}
                  />
                  <PropertiesPanel.TextOption
                    id="field-defaultValue"
                    className="[&>:first-child>*]:!min-w-0 [&>:first-child]:min-w-[5.75rem]"
                    horizontal
                    value={rest.defaultValue ?? ''}
                    name="Default Value"
                    mutate={(v) => onUpdate(id, { defaultValue: v })}
                    forceEditable={!readOnly}
                  />
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
          {!readOnly && (
            <>
              <hr className="mx-4 mt-3 mb-4 border-dark-border-solid" />
              <Button
                className="mx-4 w-[-webkit-fill-available] text-dark-error"
                intent="secondary"
                icon={<TrashIcon />}
                onClick={() => onDelete(id)}
              >
                Remove field
              </Button>
            </>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  )
}

const OptionsOption = ({
  name,
  value,
  mutate,
  readOnly,
}: {
  name: string
  value: string[]
  mutate: (v: string[]) => void
  readOnly: boolean
}) => {
  const containerRef = React.useRef<HTMLDivElement>(null)

  return (
    <div
      className={clsx(
        'grid grid-cols-[auto_1fr] gap-x-2',
        readOnly ? 'my-0.5 gap-y-0.5' : 'gap-y-2'
      )}
      ref={containerRef}
    >
      <p
        className={clsx(
          'min-w-[5.75rem] text-xs font-medium text-dark-muted',
          readOnly || 'leading-7'
        )}
      >
        {name}
      </p>
      {value.map((option, idx) => {
        if (readOnly) {
          if (!option) return null
          return (
            <span
              key={`option-${idx}`}
              className="relative col-start-2 text-xs leading-normal text-dark-bright"
            >
              {option}
            </span>
          )
        }

        return (
          <span key={`option-${idx}`} className="relative col-start-2">
            <Input
              value={option}
              size="xs"
              onChange={(e) => {
                const newOptions = [...value]
                newOptions[idx] = e.target.value
                mutate(newOptions)
              }}
            />
            <IconButton
              className="absolute top-1/2 -translate-y-1/2 right-1 text-dark-faint hover:text-dark-muted !bg-transparent"
              onClick={() => mutate(value.filter((_, i) => i !== idx))}
            >
              <Cross2Icon />
            </IconButton>
          </span>
        )
      })}
      {!readOnly && (
        <Button
          unstyled
          focusStyled
          className="flex items-center col-start-2 text-xs font-medium cursor-default text-dark-faint hover:text-dark-muted gap-x-1"
          onClick={() => {
            mutate([...value, ''])
            setTimeout(
              () =>
                (
                  containerRef.current?.querySelector(
                    'span:last-of-type>input'
                  ) as HTMLInputElement | null
                )?.focus(),
              150
            )
          }}
        >
          <PlusIcon className="size-[0.9375rem]" />
          Add option
        </Button>
      )}
    </div>
  )
}

const FieldIcon = ({
  type,
  className,
  ...props
}: { type: EventField['type'] } & IconProps) => {
  const compoundClassName = clsx(className, 'size-[0.9375rem]')
  switch (type) {
    case 'text':
      return <TextIcon {...props} className={compoundClassName} />
    case 'textarea':
      return <InputIcon {...props} className={compoundClassName} />
    case 'email':
      return <EnvelopeClosedIcon {...props} className={compoundClassName} />
    case 'checkbox':
      return <CheckboxIcon {...props} className={compoundClassName} />
    case 'number':
      return <FrameIcon {...props} className={compoundClassName} />
    case 'select':
      return <HamburgerMenuIcon {...props} className={compoundClassName} />
    case 'date':
    case 'datetime':
      return <CalendarIcon {...props} className={compoundClassName} />
    case 'file':
      return <FileTextIcon {...props} className={compoundClassName} />
    case 'radio':
      return <RadiobuttonIcon {...props} className={compoundClassName} />
    case 'hidden':
      return <EyeNoneIcon {...props} className={compoundClassName} />
    default:
      return null
  }
}

const generateFakeDataFromEventSchema = (
  schema: EventBlock['constraints']['schema']
) => {
  const fakeData: Record<string, unknown> = {}

  for (const [_fieldId, field] of Object.entries(schema)) {
    if (!field) continue
    // Skip if field is optional and we randomly decide not to include it
    if (!field.required && Math.random() > 0.7) {
      continue
    }

    switch (field.type) {
      case 'text':
        fakeData[field.name] = faker.lorem.sentence()
        break
      case 'textarea':
        fakeData[field.name] = faker.lorem.paragraph()
        break
      case 'email':
        fakeData[field.name] = faker.internet.email()
        break
      case 'checkbox':
        fakeData[field.name] = faker.datatype.boolean()
        break
      case 'number':
        fakeData[field.name] = faker.number.int({ min: 0, max: 1000 })
        break
      case 'select':
      case 'radio':
        if (field.multiple) {
          // For multiple select, pick 1-3 random options
          const numChoices = faker.number.int({ min: 1, max: 3 })
          fakeData[field.name] = faker.helpers.arrayElements(
            field.options,
            numChoices
          )
        } else {
          // For single select/radio, pick one random option
          fakeData[field.name] = faker.helpers.arrayElement(field.options)
        }
        break
      case 'date':
        fakeData[field.name] = new Date(
          faker.date.past().toISOString().split('T')[0] as string
        )
        break
      case 'datetime':
        fakeData[field.name] = faker.date.past().toISOString()
        break
      case 'file':
        fakeData[field.name] = faker.system.filePath()
        break
      case 'hidden':
        // Use default value if provided, otherwise generate random string
        fakeData[field.name] = field.defaultValue || faker.string.uuid()
        break
      default:
        field satisfies never
        break
    }

    // Override with default value if one exists and we randomly choose to use it
    if (field.defaultValue && Math.random() > 0.7) {
      fakeData[field.name] = field.defaultValue
    }
  }

  return fakeData
}
